import {gfrCalculationMethodOptions, serumUnitOptions} from '@/modules/drug-catalog/constants'
import {URINARY_COLLECTION_TIME} from '@/modules/patients/constants'
import {roundGfr} from '@/modules/patients/functions'
import {useZero} from '@/shared/integrations/zero/use-zero'
import {parseDate} from '@ark-ui/react'
import {Box, Button, ButtonGroup, Text, useDialogContext} from '@chakra-ui/react'
import {GfrMethod, SerumUnit, SerumUnitType, type GfrMethodType} from '@cypro/engine/database/schema'
import {Patient} from '@cypro/engine/schema'
import {GFR_PLAUSIBLE_MAX, isGfrPlausible, umolLToMgDl} from '@cypro/engine/utils'
import {DialogBody, DialogFooter, DialogHeader, Tooltip} from '@cypro/ui/components'
import {DisplayInput} from '@cypro/ui/components/form/display-input'
import {FormSection} from '@cypro/ui/components/form/form-section'
import {useAppForm} from '@cypro/ui/integrations/form'
import {toaster} from '@cypro/ui/toaster'
import {getAge, isDefined} from '@cypro/ui/utils'
import {useStore} from '@tanstack/react-form'
import {useTranslate} from '@tolgee/react'
import {useMemo} from 'react'
import * as z from 'zod'
import {GFRAlert} from '../gfr-alert'
import {GFRMissingDataAlert} from '../gfr-missing-data-alert'
import {calculateGfr, convertSerumCreatinine, isSerumCreatinineInRange} from './calculate-gfr'
import {useConfirmGfrDoseChange} from './gfr-dose-change-dialog'

const AddGfrSchema = z
  .object({
    method: GfrMethod,
    labTime: z.array(z.any()).nonempty(),
    serumCreatinine: z.number().optional(),
    urineCreatinine: z.number().optional(),
    gfr: z.number(),
    urineVolume: z.number().optional(),
    collectionTime: z.number().optional(),
    serumUnit: SerumUnit.optional(),
  })
  .superRefine((data, ctx) => {
    // a GFR above the plausible maximum comes from a wrong input and is never saved (CY-348)
    if (data.gfr > GFR_PLAUSIBLE_MAX) {
      ctx.addIssue({code: 'custom', path: ['gfr'], message: 'gfr.implausible_cannot_save'})
    }
    // a serum creatinine outside the plausible range is refused, e.g. 0.8 typed as µmol/L instead of mg/dL (CY-348)
    const unit = data.serumUnit ?? SerumUnit.enum.ml_dl
    if (
      data.method !== 'manual_entry' &&
      data.serumCreatinine &&
      !isSerumCreatinineInRange(data.serumCreatinine, unit)
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['serumCreatinine'],
        message: `gfr.serum_creatinine_out_of_range.${unit}`,
      })
    }
    if ((data.method === 'cockcroft_gault' || data.method === 'jelliffe') && !data.serumCreatinine) {
      ctx.addIssue({
        code: 'custom',
        path: ['serumCreatinine'],
        message: 'gfr.serum_creatinine_required',
      })
    }
    if (
      data.method === 'urinary_24h' &&
      (!data.serumCreatinine || !data.urineCreatinine || !data.urineVolume || !data.collectionTime)
    ) {
      if (!data.serumCreatinine) {
        ctx.addIssue({
          code: 'custom',
          path: ['serumCreatinine'],
          message: 'gfr.serum_creatinine_required',
        })
      }
      if (!data.urineCreatinine) {
        ctx.addIssue({
          code: 'custom',
          path: ['urineCreatinine'],
          message: 'gfr.urine_creatinine_required',
        })
      }
      if (!data.urineVolume) {
        ctx.addIssue({
          code: 'custom',
          path: ['urineVolume'],
          message: 'gfr.urine_volume_required',
        })
      }
      if (!data.collectionTime) {
        ctx.addIssue({
          code: 'custom',
          path: ['collectionTime'],
          message: 'gfr.collection_time_required',
        })
      }
    }
  })

export function AddGfrDialog({patient}: {patient: Patient}) {
  const {t} = useTranslate('common')
  const zero = useZero()
  const context = useDialogContext()
  const confirmGfrDoseChange = useConfirmGfrDoseChange(patient)

  const gfrMethodItems = gfrCalculationMethodOptions.map(value => ({label: t(`gfr.${value}`), value}))
  const serumUnitItems = serumUnitOptions.map(value => ({label: t(`serum_unit.${value}`), value}))

  const form = useAppForm({
    defaultValues: {
      method: 'cockcroft_gault' as GfrMethodType,
      labTime: [] as string[],
      serumCreatinine: 0,
      serumUnit: 'ml_dl' as SerumUnitType,
      urineCreatinine: 0,
      gfr: 0,
      urineVolume: 0,
      collectionTime: 0,
    } as z.infer<typeof AddGfrSchema>,
    validators: {
      onSubmit: AddGfrSchema,
    },
    async onSubmit({value}) {
      const method = value.method
      if (!method) return

      const labTime = value.labTime[0]
      if (!labTime) return

      // save the GFR shown on screen, so the dose preview and the AUC doses use the same value (CY-316)
      const gfr = roundGfr(value.gfr ?? 0)

      const canSave = await confirmGfrDoseChange({
        add: {id: 'new', gfr, labTime: new Date(labTime).getTime(), createdAt: Date.now(), deletedAt: null},
      })
      if (!canSave) return

      return zero.attemptMutation(
        zero.mutators.patients.addGfr({
          patientId: patient.id,
          labTime: new Date(labTime).getTime(),
          gfr,
          method,
          serumCreatinine: value.serumCreatinine,
          serumUnit: value.serumUnit,
          urineCreatinine: value.urineCreatinine,
          urineVolume: value.urineVolume,
          collectionTime: value.collectionTime,
        }),
        async mutation => {
          const data = await mutation.server
          if (!data) return
          toaster.success({title: t('success'), description: t('patient_updated_successfully')})
          onClose()
        }
      )
    },
  })

  function updateGfr() {
    const method = form.getFieldValue('method')
    const serumUnit = form.getFieldValue('serumUnit')

    const serumCreatinine =
      serumUnit === 'ml_dl'
        ? form.getFieldValue('serumCreatinine')
        : umolLToMgDl(form.getFieldValue('serumCreatinine') ?? 0)

    const urineCreatinine = form.getFieldValue('urineCreatinine')
    const urineVolume = form.getFieldValue('urineVolume')
    const collectionTime = form.getFieldValue('collectionTime')

    const calculatedGfr = calculateGfr({
      method,
      serumCreatinineMgDl: serumCreatinine,
      urineCreatinine,
      urineVolume,
      collectionTime,
      birthDate: patient.birthDate,
      weight: patient.characteristics?.[0]?.weight,
      bsa: patient.characteristics?.[0]?.bsa,
      sex: patient.sex,
    })

    form.setFieldValue('gfr', calculatedGfr ?? 0)
  }

  const [selectedMethod, currentGfr, isSubmitting] = useStore(form.store, state => {
    return [state.values.method, state.values.gfr, state.isSubmitting]
  })

  function onClose() {
    context.setOpen(false)
  }

  const showSerumCreatinine =
    selectedMethod === 'cockcroft_gault' || selectedMethod === 'jelliffe' || selectedMethod === 'urinary_24h'
  const showUrinaryFields = selectedMethod === 'urinary_24h'
  const showGfr = selectedMethod === 'manual_entry'
  // Save stays off while the result is implausible, so it is never saved by mistake (CY-348)
  const isImplausible = Boolean(currentGfr) && !isGfrPlausible(currentGfr)
  const [missingSex, missingWeight, missingBsa] = useMemo(() => {
    return [
      (selectedMethod === 'cockcroft_gault' || selectedMethod === 'jelliffe') && !patient.sex,
      selectedMethod === 'cockcroft_gault' && !patient.characteristics?.[0]?.weight,
      selectedMethod === 'jelliffe' && !patient.characteristics?.[0]?.bsa,
    ]
  }, [selectedMethod, patient])
  return (
    <form
      onSubmit={e => {
        e.preventDefault()
        form.handleSubmit()
      }}
    >
      <form.AppForm>
        <DialogHeader>{t('new_gfr_entry')}</DialogHeader>

        <DialogBody minHeight="none">
          <Box display={'flex'} flexDirection={'column'} gap="5" width="100%">
            <form.AppField
              name="method"
              mode="value"
              listeners={{
                onChange: ({value}) => {
                  form.setFieldValue('serumCreatinine', 0)
                  form.setFieldValue('urineCreatinine', 0)
                  form.setFieldValue('urineVolume', 0)
                  form.setFieldValue('collectionTime', value === 'urinary_24h' ? URINARY_COLLECTION_TIME : 0)
                  updateGfr()
                },
              }}
            >
              {field => (
                <field.Select
                  label={t('gfr_table.calculation_method')}
                  items={gfrMethodItems}
                  contentProps={{zIndex: 2000}}
                />
              )}
            </form.AppField>
            <Text fontSize={'md'} fontWeight={'medium'}>
              {t('gfr.input_data')}
            </Text>
            <FormSection variant="card" width={'100%'}>
              {(missingSex || missingWeight || missingBsa) && (
                <GFRMissingDataAlert missingSex={missingSex} missingWeight={missingWeight} />
              )}
              {(selectedMethod === 'cockcroft_gault' || selectedMethod === 'jelliffe') && (
                <Box display="flex" flexDirection="row" gap="4" width={'100%'}>
                  <DisplayInput label={t('sex')} value={patient.sex ? t(patient.sex) : ''} />
                  <DisplayInput
                    label={t('age.label')}
                    value={isDefined(patient.birthDate) ? getAge(patient.birthDate).toString() : ''}
                  />
                </Box>
              )}

              {selectedMethod === 'cockcroft_gault' && (
                <DisplayInput
                  label={t('characteristics.weight')}
                  value={patient.characteristics?.[0]?.weight?.toString() ?? ''}
                />
              )}

              {selectedMethod === 'jelliffe' && (
                <DisplayInput
                  label={t('characteristics.bsa')}
                  value={patient.characteristics?.[0]?.bsa?.toString() ?? ''}
                />
              )}

              {showUrinaryFields && (
                <>
                  <form.AppField
                    name="urineCreatinine"
                    listeners={{
                      onChange: () => {
                        updateGfr()
                      },
                    }}
                  >
                    {field => <field.NumberInput label={t('gfr.urine_creatinine')} placeholder="e.g. 1500" min={0} />}
                  </form.AppField>
                  <Box display="flex" flexDirection="row" gap="4" width={'100%'}>
                    <form.AppField
                      name="urineVolume"
                      listeners={{
                        onChange: () => {
                          updateGfr()
                        },
                      }}
                    >
                      {field => <field.NumberInput label={t('gfr.urine_volume')} placeholder="e.g. 1500" min={0} />}
                    </form.AppField>

                    <form.AppField
                      name="collectionTime"
                      listeners={{
                        onChange: () => {
                          updateGfr()
                        },
                      }}
                    >
                      {field => <field.NumberInput label={t('gfr.collection_time')} placeholder="e.g. 1440" min={0} />}
                    </form.AppField>
                  </Box>
                </>
              )}
              {selectedMethod && (
                <form.AppField name="labTime">
                  {field => (
                    <field.DatePicker
                      label={t('gfr_table.sample_date')}
                      maxDate={parseDate(new Date())}
                      minDate={isDefined(patient.birthDate) ? parseDate(new Date(patient.birthDate)) : undefined}
                      required
                    />
                  )}
                </form.AppField>
              )}

              {showSerumCreatinine && (
                <Box display="flex" flexDirection="row" gap="4" width={'100%'}>
                  <form.AppField
                    name="serumCreatinine"
                    listeners={{
                      onChange: () => {
                        updateGfr()
                      },
                    }}
                  >
                    {field => (
                      // a lab value is kept exactly as entered; the arrows still step by 0.1 (CY-350)
                      <field.NumberInput label={t('gfr_table.serum')} min={0} step={0.1} roundToStep={false} />
                    )}
                  </form.AppField>
                  <form.AppField
                    name="serumUnit"
                    mode="value"
                    listeners={{
                      onChange: ({value}) => {
                        // switching the unit converts the value, so 0.8 mg/dL becomes 70.72 µmol/L (CY-348)
                        const serumCreatinine = form.getFieldValue('serumCreatinine')
                        if (serumCreatinine && value) {
                          form.setFieldValue('serumCreatinine', convertSerumCreatinine(serumCreatinine, value))
                        }
                        updateGfr()
                      },
                    }}
                  >
                    {field => (
                      <field.Select label={t('unit')} placeholder={serumUnitItems[0].label} items={serumUnitItems} />
                    )}
                  </form.AppField>
                </Box>
              )}

              {showGfr && (
                <form.AppField name="gfr">
                  {field => <field.NumberInput label={t('gfr_value')} min={0} />}
                </form.AppField>
              )}
            </FormSection>

            <Text fontSize={'md'} fontWeight={'medium'}>
              {t('gfr.recorded_result')}
            </Text>
            <GFRAlert gfrValue={currentGfr} />
          </Box>
        </DialogBody>

        <DialogFooter>
          <ButtonGroup size="md" colorPalette="cyan" width="100%" justifyContent="flex-end">
            <Button variant="outline" colorPalette="gray" onClick={onClose}>
              {t('cancel')}
            </Button>
            <Tooltip
              content={
                isImplausible
                  ? t('gfr.implausible_cannot_save', 'A GFR above {max} mL/min cannot be saved. Check the inputs.', {
                      max: GFR_PLAUSIBLE_MAX,
                    })
                  : null
              }
            >
              <form.SubmitButton loading={isSubmitting} disabled={isSubmitting || !currentGfr || isImplausible}>
                {t('save')}
              </form.SubmitButton>
            </Tooltip>
          </ButtonGroup>
        </DialogFooter>
      </form.AppForm>
    </form>
  )
}
